// apps/web/src/utils/apiClient.ts
import { getAuth, onAuthStateChanged } from "firebase/auth";

function isDemoMode(): boolean {
    if (typeof window === "undefined") return false;
    // URLで判定（/demo 配下はデモ）
    if (window.location.pathname.startsWith("/demo")) return true;
    // 任意：フラグでもONにできる
    const ls = localStorage.getItem("demoMode");
    if (ls === "1" || ls === "true") return true;
    return false;
}

function stripQuery(path: string) {
    return path.split("?")[0];
}

// apiClient.ts

export async function apiPostFormData(path: string, fd: FormData) {
    const headers = await buildHeaders(); // ★ await 必須（Promise→実体に）
    delete (headers as any)["Content-Type"]; // boundary はブラウザに任せる

    // demo の fixtures は multipart にしない（必要ならここで分岐）
    if (isDemoMode() && stripQuery(path).startsWith("/api/")) {
        // ここは “demo では import しない” 前提なら throw でもOK
        return fetchDemo("POST", path, { note: "multipart not supported in demo" });
    }

    return fetch(`${API_BASE}${path}`, {
        method: "POST",
        body: fd,
        headers: headers as HeadersInit,
        credentials: "same-origin",
    });
}

// ✅ 追加：fixture ファイル名として安全な文字列にする（最小）
function safeForFile(s: string) {
    // 英数と . _ - は残す（あなたの既存命名に寄せる）
    return String(s).replace(/[^a-zA-Z0-9._-]/g, "_");
}

function toFixtureUrl(method: string, path: string) {
    // "/api/me/company" -> "GET__api__me__company.json"
    // "/api/x?startDate=...&endDate=..." -> "GET__api__x__startDate_...__endDate_....json"

    const [pathname, qs = ""] = path.split("?");
    const clean = pathname.replace(/^\//, "");
    const base = `${method.toUpperCase()}__${clean.replace(/\//g, "__")}`;

    // ✅ クエリ無しは従来通り
    if (!qs) return `/demo-fixtures/${base}.json`;

    // ✅ クエリありは __key_value を順番に付与
    //    ※ for..of だと TS target 次第で落ちるので forEach を使う
    const sp = new URLSearchParams(qs);
    let suffix = "";
    sp.forEach((v, k) => {
        suffix += `__${safeForFile(k)}_${safeForFile(v)}`;
    });

    return `/demo-fixtures/${base}${suffix}.json`;
}

/** demo用：fixture を返す（無ければ 404(JSON) を返して不足を可視化） */
async function fetchDemo(method: string, path: string, body?: any): Promise<Response> {
    const url = toFixtureUrl(method, path);

    const res = await fetch(url, {
        method: "GET", // fixtures は基本 GET で読む
        cache: "no-store",
    });

    // ✅ res.ok でも index.html (text/html) が返る環境があるため、JSONかを確認する
    const contentType = res.headers.get("content-type") || "";
    if (res.ok && contentType.includes("application/json")) return res;

    // ✅ ok だけど JSON じゃない（=だいたい index.html）→ missing 扱いでJSONを返す
    if (res.ok) {
        console.warn(`[demo] fixture not json (maybe index.html): ${url} content-type=${contentType}`);
        return new Response(
            JSON.stringify({
                error: "demo_fixture_not_json",
                url,
                contentType,
                method: method.toUpperCase(),
                path,
            }),
            {
                status: 404,
                headers: { "Content-Type": "application/json" },
            }
        );
    }

    // ✅ 404 の HTML を返すと res.json() で死ぬので、JSONで 404 を返す（不足ファイル名を見える化）
    console.warn(`[demo] fixture missing: ${url}`);
    return new Response(
        JSON.stringify({ error: "demo_fixture_missing", url, method: method.toUpperCase(), path }),
        {
            status: 404,
            headers: { "Content-Type": "application/json" },
        }
    );
}

async function getValidToken(): Promise<string> {
    // ✅ demo はトークン不要
    if (isDemoMode()) return "demo";

    const auth = getAuth();
    let user = auth.currentUser;

    if (!user) {
        await new Promise<void>((resolve, reject) => {
            const unsub = onAuthStateChanged(auth, (u) => {
                if (u) {
                    user = u;
                    unsub();
                    resolve();
                }
            });

            setTimeout(() => {
                unsub();
                reject(new Error("AUTH_TIMEOUT"));
            }, 5000);
        });
    }

    // 毎回 forceRefresh=true だと都度ネットワーク通信になり、
    // 一時的な回線揺らぎで auth/network-request-failed が発生しやすい。
    // まずはキャッシュトークンを使い、失敗時のみ強制更新を試す。
    try {
        return await user!.getIdToken();
    } catch {
        return await user!.getIdToken(true);
    }
}

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// ✅ 追加：imp が両方あるなら「なりすまし中」とみなす（最小）
function getImpersonation(): { tenantId: string | null; companyId: string | null } {
    if (typeof window === "undefined") return { tenantId: null, companyId: null };
    const t = String(localStorage.getItem("impTenantId") || "").trim();
    const c = String(localStorage.getItem("impCompanyId") || "").trim();
    return {
        tenantId: t ? t : null,
        companyId: c ? c : null,
    };
}

function getAmazonStorageScope(): { tenantId: string; companyId: string } {
    if (typeof window === "undefined") {
        return { tenantId: "ecvision", companyId: "default" };
    }
    const tenantId = String(localStorage.getItem("selectedTenant") || "ecvision").trim() || "ecvision";
    const companyId = String(
        localStorage.getItem("selectedCompanyId") ||
        localStorage.getItem("companyId") ||
        localStorage.getItem("accountId") ||
        "default"
    ).trim() || "default";
    return { tenantId, companyId };
}

function getScopedAmazonStorageValue(baseKey: "amazonSellerId" | "amazonSellerName"): string {
    if (typeof window === "undefined") return "";
    const scope = getAmazonStorageScope();
    const scopedKey = `${baseKey}:${scope.tenantId}:${scope.companyId}`;
    return String(localStorage.getItem(scopedKey) || "").trim();
}

function getSelectedMallHeaderContext(): { mall: string; mallShopId: string } | null {
    if (typeof window === "undefined") return null;

    const mall = String(localStorage.getItem("selectedMall") || "rakuten").trim().toLowerCase();
    const selectedShopId = String(localStorage.getItem("selectedShopId") || "").trim();

    if (selectedShopId.includes(":")) {
        const idx = selectedShopId.indexOf(":");
        const parsedMall = selectedShopId.slice(0, idx).trim().toLowerCase();
        const parsedShopId = selectedShopId.slice(idx + 1).trim();
        // 現在選択中モールと一致する場合のみ selectedShopId を優先する。
        // 以前の選択（例: amazon:...）が残っていると楽天表示でも Amazon ヘッダが送られる不具合を防ぐ。
        if (parsedMall === mall && parsedShopId && parsedShopId !== "__amazon_unconfigured__") {
            return { mall: parsedMall, mallShopId: parsedShopId };
        }
    }

    if (mall === "amazon") {
        const sellerId = getScopedAmazonStorageValue("amazonSellerId");
        return sellerId ? { mall, mallShopId: sellerId } : null;
    }

    if (mall === "yahoo") {
        // Yahoo は storeId をローカルにキャッシュしないため selectedShopId 経由のみ。
        // 楽天 shopUrl へフォールバックさせない（yahoo 選択中に rakuten ヘッダを送らない）。
        return null;
    }

    const shopUrl = String(localStorage.getItem("selectedShopUrl") || localStorage.getItem("shopUrl") || "").trim();
    return shopUrl ? { mall: "rakuten", mallShopId: shopUrl } : null;
}

/** tenantId（なければ ecvision） */
function getTenantId(): "ecvision" | "ecx" | string {
    // ✅ 最小修正：/super 以外でも impTenantId+impCompanyId があれば最優先（なりすまし維持）
    const imp = getImpersonation();
    if (imp.tenantId && imp.companyId) {
        return imp.tenantId;
    }

    // ✅ /super は従来通り（impTenantId があればそれ）
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/super")) {
        return (localStorage.getItem("impTenantId") || localStorage.getItem("selectedTenant") || "ecvision").trim();
    }

    // ✅ /login でも「直前が /super に戻す目的」なら ecvision 扱い（/super→/login の途中対策）
    const t = localStorage.getItem("selectedTenant");
    const tenant = t && t.trim() ? t.trim() : "ecvision";

    // ✅ /demo 以外では demo tenant を返さない（super/通常が demo 汚染されるのを防ぐ）
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/demo") && tenant === "demo") {
        return "ecvision";
    }
    return tenant;
}

/**
 * tenant によって送る companyId を変える
 * - ecvision: localStorage.companyId
 * - ecx:      localStorage.accountId（ECX token で確定したやつ）
 */
function getCompanyIdForHeader(): string | null {
    // ✅ 最小修正：/super 以外でも impCompanyId があれば最優先（なりすまし維持）
    const imp = getImpersonation();
    if (imp.tenantId && imp.companyId) {
        return imp.companyId;
    }

    if (typeof window !== "undefined" && window.location.pathname.startsWith("/super")) {
        const impCompanyId = localStorage.getItem("impCompanyId");
        //console.log("getCompanyIdForHeader imp:", impCompanyId);
        return impCompanyId;
    }

    const tenantId = getTenantId();
    const selectedCompanyId =
        (typeof window !== "undefined"
            ? localStorage.getItem("selectedCompanyId")
            : null) || null;
    //console.log("getCompanyIdForHeader tenantId:", tenantId);
    if (tenantId === "ecx") {
        return selectedCompanyId || localStorage.getItem("accountId") || localStorage.getItem("companyId");
    }
    return selectedCompanyId || localStorage.getItem("companyId") || localStorage.getItem("accountId");
}


// ✅ 追加：RUN_IDっぽい companyId を判定
function isRunIdLikeCompanyId(v: string | null | undefined) {
    const s = String(v ?? "").trim();
    if (!s) return false;
    return s.startsWith("rmsDataDownload__") || s.startsWith("setting__");
}

// ✅ 追加：汚染された storage を掃除（最小）
function purgeCorruptedCompanyContext() {
    if (typeof window === "undefined") return;

    const keys = ["companyId", "accountId", "impCompanyId"];
    for (const k of keys) {
        const v = localStorage.getItem(k);
        if (v && isRunIdLikeCompanyId(v)) {
            console.warn(`[apiClient] purge corrupted ${k}:`, v);
            localStorage.removeItem(k);
        }
    }
}

async function buildHeaders() {

    // ✅ 追加：ログイン後も残る汚染を最初に除去
    purgeCorruptedCompanyContext();

    // ✅ demo は Authorization を付けない（Firebase/サーバへ行かない前提）
    if (isDemoMode()) {
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
            "X-Tenant-Id": "demo",
        };
        const cid = localStorage.getItem("companyId") || "demo-company";
        headers["X-Company-Id"] = cid;
        return headers;
    }

    const token = await getValidToken();

    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
    };

    const tenantId = getTenantId();
    //console.log("buildHeaders tenantId:", tenantId);
    headers["X-Tenant-Id"] = tenantId;

    const companyId = getCompanyIdForHeader();
    //console.log("buildHeaders companyId:", companyId);
    if (companyId && companyId.trim() !== "") {
        headers["X-Company-Id"] = companyId;
    }

    if (typeof window !== "undefined") {
        const selectedMall = String(localStorage.getItem("selectedMall") || "rakuten")
            .trim()
            .toLowerCase();
        headers["X-Mall"] =
            selectedMall === "amazon" ? "amazon" : selectedMall === "yahoo" ? "yahoo" : "rakuten";
    }

    const mallContext = getSelectedMallHeaderContext();
    if (mallContext) {
        headers["X-Mall"] = mallContext.mall;
        headers["X-Mall-Shop-Id"] = mallContext.mallShopId;
    }

    return headers;
}

/** GET */
export async function apiGet(path: string) {
    // ✅ demo: /api/* は fixtures にリライト
    if (isDemoMode() && stripQuery(path).startsWith("/api/")) {
        return fetchDemo("GET", path);
    }

    return fetch(`${API_BASE}${path}`, {
        method: "GET",
        headers: await buildHeaders(),
        cache: "no-store",
    });
}

/** POST */
export async function apiPost(path: string, body?: any, opts?: { signal?: AbortSignal }) {
    if (isDemoMode() && stripQuery(path).startsWith("/api/")) {
        return fetchDemo("POST", path, body);
    }

    return fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: await buildHeaders(),
        body: body ? JSON.stringify(body) : undefined,
        signal: opts?.signal,
    });
}

/** PUT */
export async function apiPut(path: string, body?: any) {
    if (isDemoMode() && stripQuery(path).startsWith("/api/")) {
        return fetchDemo("PUT", path, body);
    }

    return fetch(`${API_BASE}${path}`, {
        method: "PUT",
        headers: await buildHeaders(),
        body: body ? JSON.stringify(body) : undefined,
    });
}

/** PATCH */
export async function apiPatch(path: string, body?: any) {
    if (isDemoMode() && stripQuery(path).startsWith("/api/")) {
        return fetchDemo("PATCH", path, body);
    }

    return fetch(`${API_BASE}${path}`, {
        method: "PATCH",
        headers: await buildHeaders(),
        body: body ? JSON.stringify(body) : undefined,
    });
}

/** DELETE */
export async function apiDelete(path: string) {
    if (isDemoMode() && stripQuery(path).startsWith("/api/")) {
        return fetchDemo("DELETE", path);
    }

    return fetch(`${API_BASE}${path}`, {
        method: "DELETE",
        headers: await buildHeaders(),
    });
}

async function parseJsonOrThrow<T>(res: Response, path: string): Promise<T> {
    if (!res.ok) {
        const contentType = res.headers.get("content-type") || "";
        const body = contentType.includes("application/json")
            ? JSON.stringify(await res.json())
            : await res.text().catch(() => "");

        if (res.status === 401) throw new Error("UNAUTHORIZED");
        throw new Error(`${res.status} ${path}: ${body}`);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

export async function apiGetJson<T = any>(path: string) {
    return parseJsonOrThrow<T>(await apiGet(path), path);
}
export async function apiPostJson<T = any>(path: string, body?: any) {
    return parseJsonOrThrow<T>(await apiPost(path, body), path);
}
export async function apiPutJson<T = any>(path: string, body?: any) {
    return parseJsonOrThrow<T>(await apiPut(path, body), path);
}
export async function apiPatchJson<T = any>(path: string, body?: any) {
    return parseJsonOrThrow<T>(await apiPatch(path, body), path);
}
export async function apiDeleteJson<T = any>(path: string, body?: any) {
    if (isDemoMode() && stripQuery(path).startsWith("/api/")) {
        return parseJsonOrThrow<T>(await fetchDemo("DELETE", path, body), path);
    }

    return parseJsonOrThrow<T>(
        await fetch(`${API_BASE}${path}`, {
            method: "DELETE",
            headers: await buildHeaders(),
            body: body ? JSON.stringify(body) : undefined,
        }),
        path
    );
}
